import { Box, Grid, Stack, Typography, useTheme, Divider } from "@mui/material";
import { WhiteBox, FoodsTooltip, FoodsIcon } from "@foodpilot/foods";
import { IndicatorsSectionProps } from ".";

export type Objective = {
    year: string;
    value: string;
    unit?: string;
};

export type IndicatorItem = {
    pilarTitle?: string;
    title: string;
    tooltip?: string;
    value?: string;
    valueColor?: string;
    unit?: string;
    objectives?: Objective[];
    index?: number;
};

const IndicatorNode = (props: IndicatorItem) => {
    const { pilarTitle, title, tooltip, value, valueColor, unit, objectives } = props;
    const theme = useTheme();

    return (
        <Grid item xs={12} sm={12} md={6} lg={4}>
            <WhiteBox
                sx={{
                    padding: theme.spacing(3, 3, 2, 3),
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    position: "relative",
                }}
            >
                {tooltip && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: theme.spacing(2.5),
                            right: theme.spacing(2),
                            zIndex: 1,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <FoodsTooltip
                            title={tooltip}
                            placement="top"
                            arrow
                            width="unset"
                            enterTouchDelay={0}
                            leaveTouchDelay={0}
                        >
                            <Box component="span" sx={{ display: "flex" }}>
                                <FoodsIcon icon="information" size={3} />
                            </Box>
                        </FoodsTooltip>
                    </Box>
                )}
                <Stack sx={{ position: "relative" }} gap={theme.spacing(1)}>
                    {pilarTitle && (
                        <Typography
                            variant="caption"
                            component="div"
                            color={theme.custom.grey[1600]}
                            sx={{
                                fontSize: theme.typography.caption.fontSize,
                                fontWeight: theme.typography.fontWeightMedium,
                                lineHeight: 1.5,
                                paddingRight: tooltip ? theme.spacing(4) : 0,
                            }}
                        >
                            {pilarTitle}
                        </Typography>
                    )}

                    <Box sx={{ position: "relative" }}>
                        {title && (
                            <Typography
                                variant="h3"
                                component="div"
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    pr: tooltip ? theme.spacing(4) : 0,
                                }}
                            >
                                {title}
                            </Typography>
                        )}
                    </Box>
                </Stack>
                <Box sx={{ flexGrow: 1 }} />

                {value && (
                    <Stack
                        sx={{ my: theme.spacing(2) }}
                        direction="row"
                        gap={theme.spacing(0.75)}
                        alignItems="flex-end"
                    >
                        <Typography
                            variant="h2"
                            component="div"
                            color={valueColor || theme.palette.success.main}
                            sx={{
                                fontSize: theme.typography.h1.fontSize,
                                fontWeight: theme.typography.fontWeightBold,
                                lineHeight: 1.2,
                                display: "inline",
                            }}
                        >
                            {value}
                        </Typography>
                        {unit && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: theme.typography.body1.fontSize,
                                    fontWeight: theme.typography.fontWeightRegular,
                                    color: theme.custom.grey[1600],
                                    verticalAlign: "middle",
                                }}
                            >
                                {unit}
                            </Typography>
                        )}
                    </Stack>
                )}

                {objectives && objectives.length > 0 && (
                    <Box>
                        <Divider
                            sx={{
                                mb: theme.spacing(2),
                                width: `calc(100% + ${theme.spacing(6)})`,
                                ml: theme.spacing(-3),
                            }}
                        />
                        <Stack spacing={theme.spacing(1)}>
                            {objectives.map(
                                (objective, idx) =>
                                    objective.value &&
                                    objective.year && (
                                        <Stack
                                            key={idx}
                                            direction="row"
                                            gap={theme.spacing(1)}
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography
                                                variant="h4"
                                                component="div"
                                                sx={{
                                                    mr: theme.spacing(1),
                                                }}
                                            >
                                                {`${objective.year}`}
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                gap={theme.spacing(0.5)}
                                                sx={{
                                                    bgcolor: "#FEFCFB",
                                                    borderRadius: theme.spacing(1),
                                                    border: `1px solid rgba(0, 0, 0, 0.12)`,
                                                    px: theme.spacing(1.5),
                                                    py: theme.spacing(0.5),
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    component="div"
                                                    sx={{ fontWeight: theme.typography.fontWeightBold }}
                                                >
                                                    {objective.value}
                                                </Typography>
                                                {objective.unit && (
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        color={theme.custom.grey[1600]}
                                                        sx={{ ml: theme.spacing(0.5) }}
                                                    >
                                                        {objective.unit}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Stack>
                                    ),
                            )}
                        </Stack>
                    </Box>
                )}
            </WhiteBox>
        </Grid>
    );
};

export const IndicatorsSection = (props: IndicatorsSectionProps) => {
    const { indicators } = props;
    const indicatorItems = indicators?.length > 0 ? indicators : [];

    return (
        <Grid container spacing={2}>
            {indicatorItems.map((indicator, index) => (
                <IndicatorNode
                    key={index}
                    title={indicator.title}
                    tooltip={indicator.tooltip}
                    pilarTitle={indicator.pilarTitle}
                    value={indicator.value}
                    valueColor={indicator.valueColor}
                    unit={indicator.unit}
                    objectives={indicator.objectives}
                />
            ))}
        </Grid>
    );
};
